import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const EnrollmentManagement = () => {
  const navigate = useNavigate();
  const [enrollmentApplications, setEnrollmentApplications] = useState([]);
  const [enrollmentStats, setEnrollmentStats] = useState({ pending: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentAdmin, setCurrentAdmin] = useState(null);

  useEffect(() => {
    loadCurrentAdmin();
    loadEnrollmentData();
  }, []);

  const loadCurrentAdmin = () => {
    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    setCurrentAdmin(adminUser);
  };

  const loadEnrollmentData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.ENROLLMENTS}`);
      if (response.ok) {
        const enrollments = await response.json();
        setEnrollmentApplications(enrollments);

        // Update stats
        const stats = {
          pending: enrollments.filter(e => e.status === 'pending').length,
          total: enrollments.length,
          approved: enrollments.filter(e => e.status === 'approved').length,
          rejected: enrollments.filter(e => e.status === 'rejected').length,
          underReview: enrollments.filter(e => e.status === 'under_review').length
        };
        setEnrollmentStats(stats);
      }
    } catch (error) {
      console.error('Error loading enrollment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus, comments = '') => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/enrollments/${applicationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          reviewComments: comments,
          adminId: currentAdmin?.id || 'admin'
        })
      });

      if (response.ok) {
        alert(`Application ${newStatus === 'approved' ? 'approved' : 'rejected'} successfully!`);
        loadEnrollmentData(); // Refresh the data
      } else {
        alert('Failed to update application status');
      }
    } catch (error) {
      console.error('Error updating application:', error);
      alert('Failed to update application status');
    }
  };

  const approveApplication = (applicationId) => {
    const comments = prompt('Optional approval comments:');
    if (comments !== null) { // User didn't cancel
      handleStatusUpdate(applicationId, 'approved', comments);
    }
  };

  const rejectApplication = (applicationId) => {
    const comments = prompt('Reason for rejection (required):');
    if (comments && comments.trim()) {
      handleStatusUpdate(applicationId, 'rejected', comments);
    } else if (comments !== null) {
      alert('Rejection reason is required');
    }
  };

  const filteredApplications = enrollmentApplications.filter(app => {
    const matchesSearch = searchQuery === '' ||
      app.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.parentEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.className.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading enrollment applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Enrollment Management</h1>
              <p className="text-gray-600 mt-1">Review and manage student enrollment applications</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition duration-200"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-2xl font-bold text-blue-600">{enrollmentStats.total}</div>
            <div className="text-sm text-gray-600">Total Applications</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-2xl font-bold text-yellow-600">{enrollmentStats.pending}</div>
            <div className="text-sm text-gray-600">Pending Review</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-2xl font-bold text-green-600">{enrollmentStats.approved}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-2xl font-bold text-red-600">{enrollmentStats.rejected}</div>
            <div className="text-sm text-gray-600">Rejected</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-2xl font-bold text-purple-600">{enrollmentStats.underReview}</div>
            <div className="text-sm text-gray-600">Under Review</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Applications</label>
              <input
                type="text"
                placeholder="Search by name, email, or class..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="under_review">Under Review</option>
              </select>
            </div>
          </div>
        </div>

        {/* Applications List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">Applications</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {enrollmentApplications.length === 0 ? 'No Applications Yet' : 'No Matching Applications'}
              </h3>
              <p className="text-gray-500">
                {enrollmentApplications.length === 0
                  ? 'Enrollment applications will appear here once parents/students submit them through the website.'
                  : 'Try adjusting your search or filter criteria.'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Class
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Parent Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {application.firstName} {application.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              Age: {application.age}, {application.gender}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          {application.className}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{application.parentEmail}</div>
                        <div className="text-sm text-gray-500">{application.parentPhone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          application.status === 'approved' ? 'bg-green-100 text-green-800' :
                          application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {application.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(application.submittedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {application.status === 'pending' && (
                            <>
                              <button
                                onClick={() => approveApplication(application.id)}
                                className="text-green-600 hover:text-green-900 px-2 py-1 rounded text-sm"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => rejectApplication(application.id)}
                                className="text-red-600 hover:text-red-900 px-2 py-1 rounded text-sm"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => {
                              setSelectedApplication(application);
                              setShowDetailsModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 rounded text-sm"
                          >
                            View Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Application Details Modal */}
      {showDetailsModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Application Details - {selectedApplication.firstName} {selectedApplication.lastName}
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Student Information */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800 mb-3">Student Information</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Name:</strong> {selectedApplication.firstName} {selectedApplication.lastName}</p>
                    <p><strong>Date of Birth:</strong> {selectedApplication.dateOfBirth}</p>
                    <p><strong>Age:</strong> {selectedApplication.age}</p>
                    <p><strong>Gender:</strong> {selectedApplication.gender}</p>
                    <p><strong>Class:</strong> {selectedApplication.className}</p>
                    <p><strong>Previous School:</strong> {selectedApplication.previousSchool || 'N/A'}</p>
                    <p><strong>Medical Conditions:</strong> {selectedApplication.medicalConditions || 'None'}</p>
                    <p><strong>Allergies:</strong> {selectedApplication.allergies || 'None'}</p>
                  </div>
                </div>

                {/* Parent Information */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-800 mb-3">Parent/Guardian Information</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Name:</strong> {selectedApplication.parentFirstName} {selectedApplication.parentLastName}</p>
                    <p><strong>Relationship:</strong> {selectedApplication.relationship}</p>
                    <p><strong>Email:</strong> {selectedApplication.parentEmail}</p>
                    <p><strong>Phone:</strong> {selectedApplication.parentPhone}</p>
                    <p><strong>Alt Phone:</strong> {selectedApplication.parentAltPhone || 'N/A'}</p>
                    <p><strong>Occupation:</strong> {selectedApplication.parentOccupation || 'N/A'}</p>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-800 mb-3">Address</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Street:</strong> {selectedApplication.address}</p>
                    <p><strong>City:</strong> {selectedApplication.city}</p>
                    <p><strong>State:</strong> {selectedApplication.state || 'N/A'}</p>
                    <p><strong>Postal Code:</strong> {selectedApplication.postalCode || 'N/A'}</p>
                    <p><strong>Country:</strong> {selectedApplication.country || 'N/A'}</p>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-red-800 mb-3">Emergency Contact</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Name:</strong> {selectedApplication.emergencyName}</p>
                    <p><strong>Relationship:</strong> {selectedApplication.emergencyRelationship}</p>
                    <p><strong>Phone:</strong> {selectedApplication.emergencyPhone}</p>
                    <p><strong>Alt Phone:</strong> {selectedApplication.emergencyAltPhone || 'N/A'}</p>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="bg-yellow-50 p-4 rounded-lg md:col-span-2">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3">Additional Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p><strong>Transportation:</strong> {selectedApplication.transportation || 'Not specified'}</p>
                      <p><strong>Lunch Program:</strong> {selectedApplication.lunchProgram || 'Not specified'}</p>
                      <p><strong>Special Needs:</strong> {selectedApplication.specialNeeds || 'None'}</p>
                    </div>
                    <div>
                      <p><strong>Interests:</strong> {selectedApplication.interests || 'Not specified'}</p>
                      <p><strong>Comments:</strong> {selectedApplication.comments || 'None'}</p>
                    </div>
                  </div>
                </div>

                {/* Application Status */}
                <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Application Status</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <p><strong>Status:</strong>
                      <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                        selectedApplication.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        selectedApplication.status === 'approved' ? 'bg-green-100 text-green-800' :
                        selectedApplication.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {selectedApplication.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </p>
                    <p><strong>Submitted:</strong> {formatDate(selectedApplication.submittedAt)}</p>
                    <p><strong>Reviewed:</strong> {selectedApplication.reviewedAt ? formatDate(selectedApplication.reviewedAt) : 'Not reviewed'}</p>
                  </div>

                  {selectedApplication.reviewComments && (
                    <div className="mt-3 p-3 bg-white rounded border">
                      <p className="font-medium text-gray-700">Review Comments:</p>
                      <p className="text-gray-600">{selectedApplication.reviewComments}</p>
                    </div>
                  )}

                  {/* Action Buttons for Pending Applications */}
                  {selectedApplication.status === 'pending' && (
                    <div className="flex space-x-3 mt-4">
                      <button
                        onClick={() => {
                          approveApplication(selectedApplication.id);
                          setShowDetailsModal(false);
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                      >
                        Approve Application
                      </button>
                      <button
                        onClick={() => {
                          rejectApplication(selectedApplication.id);
                          setShowDetailsModal(false);
                        }}
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                      >
                        Reject Application
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrollmentManagement;
